import { useState, useEffect, useCallback } from 'react';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const GA_MEASUREMENT_ID = 'G-F6E059PERK';

type ConsentStatus = 'accepted' | 'declined' | null;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentStatus>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [gaLoaded, setGaLoaded] = useState(false);

  // Load consent status from localStorage on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus;
    setConsent(storedConsent);
    setIsLoaded(true);

    // If already accepted, load GA
    if (storedConsent === 'accepted') {
      loadGoogleAnalytics();
    }
  }, []);

  const loadGoogleAnalytics = useCallback(() => {
    if (gaLoaded) return;
    
    // Create and append the gtag script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID);

    setGaLoaded(true);
  }, [gaLoaded]);

  const accept = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setConsent('accepted');
    loadGoogleAnalytics();
  }, [loadGoogleAnalytics]);

  const decline = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setConsent('declined');
  }, []);

  const showBanner = isLoaded && consent === null;

  return {
    consent,
    isLoaded,
    showBanner,
    accept,
    decline,
  };
}
